import {
  RETURN_PATH_PARAM,
  SESSION_EXPIRED_PARAM,
  SESSION_EXPIRED_VALUE,
} from '@/constants/authRedirect'
import { isNoAuthRoute, isPublicRoute } from '@/middleware'

/**
 * URL 파서가 **구조 해석 전에** 제거하는 문자들.
 *
 * `"/\t/evil.com"` 은 `startsWith('/')` 를 통과하고 `//` 로도 시작하지 않지만,
 * 브라우저는 탭을 먼저 지우고 `//evil.com` 으로 읽어 외부 origin으로 나간다.
 * 즉 **검사하는 문자열과 파서가 보는 문자열이 다르므로** prefix 검사만으로는 막을 수 없다.
 */
const URL_PARSER_STRIPPED = /[\t\n\r]/g

/** origin 비교용 고정 base. 이동 대상이 아니라 판정 기준일 뿐이다. */
const ORIGIN_PROBE_BASE = 'https://return-path-probe.invalid'

/**
 * `from` 으로 돌려보내도 안전한 내부 경로인지 검사한다.
 *
 * 이 값은 URL 쿼리에서 오므로 신뢰할 수 없다. 통과 조건:
 * - **파싱했을 때** 우리 origin에 머무를 것 (외부 URL·protocol-relative·백슬래시·
 *   탭/개행 삽입 우회를 한 번에 차단한다)
 * - sign_in 등 NO_AUTH 라우트가 아닐 것 — 자기 자신으로 돌아가는 고리를 만든다
 * - PUBLIC 라우트가 아닐 것 — 애초에 인증이 필요 없어 복귀 대상이 아니다
 */
export const isSafeReturnPath = (path?: string | null): path is string => {
  if (!path || typeof path !== 'string') return false
  if (!path.startsWith('/')) return false

  // 파서와 동일한 시야로 정규화한 뒤 origin을 실제로 확인한다.
  const normalized = path.replace(URL_PARSER_STRIPPED, '')
  let resolved: URL
  try {
    resolved = new URL(normalized, ORIGIN_PROBE_BASE)
  } catch {
    return false
  }
  if (resolved.origin !== ORIGIN_PROBE_BASE) return false
  // `/\evil.com` 처럼 파서가 authority로 읽는 형태도 위에서 걸러지지만,
  // 정규화 후 다시 `/` 로 시작하는지까지 확인해 두 번 막는다.
  if (!normalized.startsWith('/') || normalized.startsWith('//')) return false

  const pathname = resolved.pathname
  if (isNoAuthRoute(pathname) || isPublicRoute(pathname)) return false

  return true
}

/** 현재 보고 있는 경로(쿼리 포함)를 복귀 대상 후보로 만든다. 안전하지 않으면 null. */
export const currentReturnPath = (): string | null => {
  if (typeof window === 'undefined') return null
  const path = `${window.location.pathname}${window.location.search}`
  return isSafeReturnPath(path) ? path : null
}

/** 인터셉터가 쓰는 sign_in URL 조립. 여기는 항상 "세션이 죽었다"가 확인된 경로다. */
export const buildSignInUrl = (returnPath?: string | null): string => {
  const params = new URLSearchParams()
  if (isSafeReturnPath(returnPath)) params.set(RETURN_PATH_PARAM, returnPath)
  params.set(SESSION_EXPIRED_PARAM, SESSION_EXPIRED_VALUE)
  return `/users/sign_in?${params.toString()}`
}
